import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { useForm } from 'react-hook-form';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import { ReactComponent as ShowPasswordIcon } from '../../assets/svg/ShowPasswordIcon.svg';
import { ReactComponent as HidePasswordIcon } from '../../assets/svg/HidePasswordIcon.svg';
import '../../styles/modals/StudentInfoModal.css';
import Loading from '../Loading';
import { changeUserPassword } from '../../server/server';
import { notify } from '../../utils/util';

const MIN_PASSWORD_LENGTH = 8;

function ChangePasswordModal({ modalIsOpen, closeModal, user, reloadList }: { modalIsOpen: boolean; closeModal: () => void; user: any; reloadList?: () => void }) {
	const [processing, setProcessing] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const { register, handleSubmit, getValues, reset, formState } = useForm<{ password: string }>({
		mode: 'onChange',
		defaultValues: { password: '' },
	});
	const { errors } = formState;

	const cleanUpFunction = () => {
		reset();
		setShowPassword(false);
	};

	const onSubmit = async () => {
		if (!user?.id) return;

		setProcessing(true);
		try {
			const resp = await changeUserPassword({
				userId: user.id,
				password: getValues().password,
			});

			if (resp.status === 200 || resp.status === 201) {
				notify('Password changed successfully!');
				reloadList?.();
				closeModal();
			} else {
				notify('Failed to change password');
			}
		} catch (error) {
			console.error(error);
			notify('Unexpected error changing password');
		} finally {
			setProcessing(false);
		}
	};

	const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
	const title = fullName ? `Change password for ${fullName}` : 'Change password';

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterClose={cleanUpFunction}
			onRequestClose={closeModal}
			style={{
				overlay: {
					zIndex: 7,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 600,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal'>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={closeModal}>
						<CloseModalIcon />
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center'>
						<div className='flex-1'>
							<p className='text-primaryViolet font-semibold text-3xl text-left'>{title}</p>
						</div>
					</div>

					<div className='mt-5'>
						<p className='text-primaryViolet font-semibold text-lg text-left'>New password</p>
						<div className={`border-b p-2 mt-2 flex flex-row items-center ${errors.password ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
							<input
								type={showPassword ? 'text' : 'password'}
								className={`w-full text-primaryViolet ${errors.password ? 'placeholder:text-redError' : ''}`}
								placeholder='Type new password'
								autoComplete='new-password'
								{...register('password', {
									required: true,
									minLength: MIN_PASSWORD_LENGTH,
								})}
								style={{ backgroundColor: 'transparent' }}
							/>
							<button
								type='button'
								className='cursor-pointer p-1 ml-2 rounded-full hover:bg-greyLight/30 transition-colors duration-200 focus:outline-none'
								onClick={() => setShowPassword((prev) => !prev)}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
								tabIndex={0}
							>
								{!showPassword ? <ShowPasswordIcon className='w-5 h-5 text-greyMedium' /> : <HidePasswordIcon width={20} height={18} className='text-greyMedium' />}
							</button>
						</div>
						{errors.password?.type === 'required' && <p className='text-redError mt-1 text-sm'>*This field is required</p>}
						{errors.password?.type === 'minLength' && <p className='text-redError mt-1 text-sm'>*Password must be at least {MIN_PASSWORD_LENGTH} characters</p>}
					</div>

					<div className='flex flex-row justify-end w-full gap-5 mt-2'>
						<button className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-primaryViolet min-h-[60px] button-save-class transition' disabled={processing}>
							{processing ? (
								<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
							) : (
								<div className='flex flex-row items-center justify-center'>
									<p className='text-[18px] leading-5 font-semibold mr-3'>Save</p>
									<Save />
								</div>
							)}
						</button>
					</div>
				</form>
			</div>
		</ReactModal>
	);
}

export default ChangePasswordModal;
